import type {
  RouteMachineContext,
  RouteMachineEmitted,
  signMachine,
} from '@polaris/core/machines'
import { ChainType } from '@polaris/core/models'
import {
  formatCoin,
  LockedWalletError,
  SignIntoWalletError,
} from '@polaris/core/utils'
import type { OutputFrom } from 'xstate'
import type { Level, LogEvent } from '@/src/services/logger'

export function logEventFromRouteMachine(
  event: RouteMachineEmitted,
  context: RouteMachineContext,
): LogEvent | undefined {
  let level: Level = 'info'
  const route = context.route

  if (
    event.type === 'routeStarted' &&
    (process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ||
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'development')
  ) {
    console.table({
      autopilot: context.shouldUseMpc ? 'on' : 'off',
    })
  }

  const structuredData = {
    route_id: route?.ID,
    sourceChain: `${route?.sourceChain.chain_name} (${route?.sourceChain.chain_id ?? 'MISSING'})`,
    destinationChain: `${route?.destinationChain.chain_name} (${route?.destinationChain.chain_id ?? 'MISSING'})`,
    amountIn:
      route &&
      formatRouteAmount(
        route.InputAssetMetadata,
        route.AmountIn,
        route.sourceChain.chain_name,
      ),
    amountOut:
      route &&
      formatRouteAmount(
        route.OutputAssetMetadata,
        route.AmountOut,
        route.destinationChain.chain_name,
      ),
    duration: context.startedAtMs
      ? Date.now() - context.startedAtMs
      : undefined,
    gasFunded: context.gasFunded,
  }

  if (
    event.type === 'currentStepSignBroadcastFailed' ||
    event.type === 'currentStepSignBroadcastApprovalFailed'
  ) {
    level = 'error'

    const e = 'error' in event ? event.error : context.error

    let errorMessage = ''

    if (e) {
      errorMessage = e.message

      if (e.cause && typeof e.cause === 'object' && 'message' in e.cause) {
        errorMessage += `. Cause: ${e.cause.message}`

        if (
          e.cause instanceof SignIntoWalletError ||
          e.cause instanceof LockedWalletError
        ) {
          return undefined
        }
      }
    }

    const approval = event.type === 'currentStepSignBroadcastApprovalFailed'

    const errorData = {
      ...structuredData,
      failedTask: 'step',
      approval,
      error: e,
    }

    return {
      message: `route-tx-step-machine signAndBroadcast error: ${event.type}${errorMessage ? ` - ${errorMessage}` : ''}`,
      level,
      context: errorData,
      ...(errorData.error && { error: errorData.error }),
    }
  }

  // Handle error cases
  if (event.type === 'routeFailed' || event.type === 'retryableError') {
    level = 'error'

    const e = 'error' in event ? event.error : context.error

    let errorMessage = ''
    if (e) {
      errorMessage = e.message

      if (e.cause && typeof e.cause === 'object' && 'message' in e.cause) {
        errorMessage += `. Cause: ${e.cause.message}`
      }
    }

    const errorData = {
      ...structuredData,
      failedTask: event.for,
      error: e,
    }

    return {
      message: `route-machine error: ${event.type}${errorMessage ? ` - ${errorMessage}` : ''}`,
      level,
      context: errorData,
      ...(errorData.error && { error: errorData.error }),
    }
  }

  // Handle wallet disconnection as warnings
  if (event.type === 'walletDisconnected') {
    level = 'warn'
    return {
      message: `route machine: ${event.type}`,
      level,
      context: {
        ...structuredData,
        disconnectedDuringStep: event.step,
      },
    }
  }

  // Handle events with results
  if ('result' in event) {
    const { type, ...otherEventData } = event

    // Special interest in outputs from sign machines, surface in log message directly
    let signMachineOutput: OutputFrom<typeof signMachine> | undefined

    if ('gasPaymentResult' in event.result) {
      signMachineOutput = event.result.gasPaymentResult
    }

    if ('stepTxResult' in event.result) {
      signMachineOutput = event.result.stepTxResult
    }

    if (event.type === 'recoveringAutopilotFundsComplete') {
      signMachineOutput = event.result
    }

    let stepNum = ''
    if (event.type === 'stepComplete') {
      stepNum = `:${event.stepNumber}`
    }

    const message = signMachineOutput
      ? `route-machine: ${type}${stepNum}, ${messageFromSignMachineOutput(signMachineOutput)}`
      : `route-machine: ${type}${stepNum}`

    return {
      message,
      level,
      context: {
        ...structuredData,
        ...otherEventData,
      },
    }
  }

  if (event.type === 'stepStarted') {
    return {
      message: `route-machine: ${event.type}:${event.stepNumber}`,
      level,
      context: structuredData,
    }
  }

  // Default case
  return {
    message: `route-machine: ${event.type}`,
    level,
    context: structuredData,
  }
}

function formatRouteAmount(
  metadata: { ticker: string; denom: string } | undefined,
  amount: string | number | undefined,
  chainName?: string,
): string | undefined {
  if (!metadata || amount === undefined) return undefined
  const numericAmount =
    typeof amount === 'string' ? Number.parseFloat(amount) : amount
  return `${formatCoin({
    denom: metadata.ticker,
    amount: numericAmount,
  })}${chainName ? `:${chainName}` : ''} (${metadata.denom})`
}

function messageFromSignMachineOutput(
  output: OutputFrom<typeof signMachine>,
): string {
  return `sign-machine: chain:${output?.chainId ?? (output?.chainType ? ChainType[output.chainType] : 'MISSING')} address:${output?.signerAddress ?? 'MISSING'} tx:${output?.txHash ?? 'MISSING'}`
}
